import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';
import Company from './Company';
import { Container } from './Company/Detail/style';
import { ButtonWrapper } from 'components/Resume/style';
import Link from 'components/Link';
import { IconButton } from 'components/buttons';
import { useLocale, useTranslation } from 'hooks';
import { format, min, max, isValid, parseISO } from 'date-fns';

function CompanyGroup({ className, data, editPath, indexedMeta, language, expandCollapse }) {
  const location = useLocation();
  const { tr } = useTranslation();
  const locale = useLocale(language);

  const addCompanyGroupState = useMemo(
    () => ({
      background: location,
      listOfComp: data
    }),
    [location, data]
  );

  function getTimeDiff(from, to) {
    if (!from) return { diff: null, period: '' };
    const date1 = typeof from === 'string' ? parseISO(from) : from;
    const date2 = to ? (typeof to === 'string' ? parseISO(to) : to) : new Date();
    return date2.getTime() - date1.getTime();
  }

  function getTimeOfExp(experience) {
    if (!experience || !experience.dateFrom) return { years: 0, months: 0, text: '-' };

    const diffDate = new Date(getTimeDiff(experience.dateFrom, experience.dateTo));
    const totalMonthsDiff =
      (diffDate.getUTCFullYear() - 1970) * 12 +
      diffDate.getUTCMonth() +
      (diffDate.getDate() >= 15 ? 1 : 0);
    const [years, months] = [Math.floor(totalMonthsDiff / 12), totalMonthsDiff % 12];
    const yearsText = years <= 1 ? tr('label.year') : tr('label.years');
    const monthsText = months <= 1 ? tr('label.month') : tr('label.months');
    const yearToShow = years > 0 ? `${years} ${yearsText} ` : '';
    const monthToShow = months > 0 ? `${months} ${monthsText}` : '';
    return { years, months, text: (yearToShow + monthToShow).trim() };
  }

  function getTimeOfCompanyExp(company) {
    if (!company) return null;
    if (company.experiences.length === 1) return getTimeOfExp(company.experiences[0]);

    const totalMonthsDiff = company.experiences.reduce((total, exp) => {
      const diff = getTimeOfExp(exp);
      return total + diff.years * 12 + diff.months;
    }, 0);

    const [years, months] = [Math.floor(totalMonthsDiff / 12), totalMonthsDiff % 12];
    const yearsText = years <= 1 ? tr('label.year') : tr('label.years');
    const monthsText = months <= 1 ? tr('label.month') : tr('label.months');
    const yearToShow = years > 0 ? `${years} ${yearsText} ` : '';
    const monthToShow = months > 0 ? `${months} ${monthsText}` : '';
    return { years, months, text: (yearToShow + monthToShow).trim() };
  }

  const companyData = useMemo(() => {
    if (!data) return null;

    return data.companies.map(company => {
      company.timeInfo = {
        to: company.isActualEngagement
          ? tr('label.present')
          : company.dateTo
          ? format(parseISO(company.dateTo), 'LLL yyyy', { locale })
          : '-',
        from: company.dateFrom ? format(parseISO(company.dateFrom), 'LLL yyyy', { locale }) : '-',
        period: getTimeOfCompanyExp(company).text,
        years: getTimeOfCompanyExp(company).years,
        months: getTimeOfCompanyExp(company).months
      };

      company.experiences = company.experiences.map(exp => ({
        ...exp,
        timeInfo: {
          to: exp.isActualEngagement
            ? tr('label.present')
            : exp.dateTo
            ? format(parseISO(exp.dateTo), 'LLL yyyy', { locale })
            : '-',
          from: exp.dateFrom ? format(parseISO(exp.dateFrom), 'LLL yyyy', { locale }) : '-',
          period: getTimeOfExp(exp).text
        }
      }));

      return company;
    });
  }, [data, locale, tr]);

  const groupTimeInfo = useMemo(() => {
    if (!data) return { years: 0, months: 0, period: '', from: '-', to: '-' };

    let info = { years: 0, months: 0, period: '', from: '', to: '' };
    let groupFrom = [];
    let groupTo = [];

    data.companies.forEach(comp => {
      info.years += comp.timeInfo.years;
      info.months += comp.timeInfo.months;

      const fromDate = comp.dateFrom ? parseISO(comp.dateFrom) : null;
      const toDate = comp.isActualEngagement ? null : comp.dateTo ? parseISO(comp.dateTo) : null;

      if (isValid(fromDate)) groupFrom.push(fromDate);
      if (isValid(toDate)) groupTo.push(toDate);
    });

    info.from = groupFrom.length ? format(min(groupFrom), 'LLL yyyy', { locale }) : '-';
    info.to = data.companies.some(comp => comp.isActualEngagement)
      ? tr('label.present')
      : groupTo.length
      ? format(max(groupTo), 'LLL yyyy', { locale })
      : '-';

    const totalMonthsDiff = info.years * 12 + info.months;
    const [years, months] = [Math.floor(totalMonthsDiff / 12), totalMonthsDiff % 12];
    const yearsText = years <= 1 ? tr('label.year') : tr('label.years');
    const monthsText = months <= 1 ? tr('label.month') : tr('label.months');
    info.period =
      (years > 0 ? `${years} ${yearsText} ` : '') + (months > 0 ? `${months} ${monthsText}` : '');
    return info;
  }, [data, locale, tr]);

  // Apply series of sorting after each cycle
  data.companies && data.companies.sort(function(a, b) {
    var keyA = new Date(a.dateTo),
        keyB = new Date(b.dateTo);
    const aOngoing = a.dateTo === '';
    const bOngoing = b.dateTo === '';
    if (aOngoing && bOngoing) return 0;
    if (aOngoing) return -1;
    if (bOngoing) return 1;
    if (keyA > keyB) return -1;
    if (keyA < keyB) return 1;
    return 0;
  });

  // Sort based on start date if end dates are equal
  data.companies && data.companies.sort(function(a, b) {
    var keyC = new Date(a.dateFrom),
        keyD = new Date(b.dateFrom);

    if (a.dateTo == b.dateTo && keyC > keyD) {
      return -1;
    }
    if (a.dateTo == b.dateTo && keyC < keyD) {
      return 1;
    }
    return 0;
  });

  // Now again sort the sorted array for start date for open ended end date
  data.companies && data.companies.sort(function(a, b) {
    var keyC = new Date(a.dateFrom),
        keyD = new Date(b.dateFrom);

    if (a.dateTo == '' && keyC > keyD) {
      return -1;
    }
    if(b.dateTo == '' && keyC < keyD) {
      return 1;
    }
    return 0;
  });

  return (
    <Container className={className}>
      {data.companyGroupName ? (
        <div className="clt compParent">
          <ButtonWrapper>
            <div>
              <h2 className="WexpGroupName">
                {groupTimeInfo.from} - {groupTimeInfo.to} | {data.companyGroupName}
              </h2>
            </div>
            <IconButton
              className="CompanyGroup-btn"
              component={Link}
              icon="Edit"
              listOfComp={data.companies}
              to={`add-company-group/${data.companyGroupId}`}
              state={addCompanyGroupState}
            />
          </ButtonWrapper>

          <ul className="list">
            {data.companies.map((item, index) => (
              <li className="listItem" key={index}>
                <Company
                  data={item}
                  editPath={editPath}
                  indexedMeta={indexedMeta}
                  language={language}
                  expandCollapse={expandCollapse}
                />
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="clt compParent">
          <ButtonWrapper className="add-comp-grp">
            <IconButton
              className="compGroup-btn"
              component={Link}
              icon="Plus"
              listOfComp={data.companies}
              to="add-company-group"
              state={addCompanyGroupState}
            />
            <div>
              <Link listOfComp={data.companies} state={addCompanyGroupState} to="add-company-group">
                <span>{tr('title.addCompanyGroup')}</span>
              </Link>
            </div>
          </ButtonWrapper>

          {data.companies.map((item, index) => (
            <Company
              data={item}
              editPath={editPath}
              indexedMeta={indexedMeta}
              key={index}
              language={language}
              expandCollapse={expandCollapse}
            />
          ))}
        </div>
      )}
    </Container>
  );
}

CompanyGroup.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape({
    companyGroupId: PropTypes.string,
    companyGroupName: PropTypes.string,
    companies: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  editPath: PropTypes.string,
  indexedMeta: PropTypes.object.isRequired,
  language: PropTypes.string.isRequired,
  expandCollapse: PropTypes.bool
};

export default CompanyGroup;
